import React from 'react';

import { Callout } from './shared';

interface ToolGroup {
  category: string;
  tools: [string, string][];
}

const ToolTable: React.FC<{ groups: ToolGroup[] }> = ({ groups }) => (
  <div className="space-y-4">
    {groups.map((group) => (
      <div key={group.category}>
        <div className="text-xs font-semibold uppercase tracking-widest text-base-content/40 mb-2">
          {group.category}
        </div>
        <div className="overflow-x-auto rounded-lg shadow-card">
          <table className="table table-xs w-full">
            <tbody>
              {group.tools.map(([name, desc]) => (
                <tr key={name} className="hover:bg-base-200/50">
                  <td className="w-2/5 py-2 pl-3 pr-2">
                    <code className="text-xs text-primary">{name}</code>
                  </td>
                  <td className="py-2 pr-3 text-xs text-base-content/60">{desc}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    ))}
  </div>
);

const AgentCollapse: React.FC<{ title: string; count: number; defaultOpen?: boolean; children: React.ReactNode }> = ({
  title,
  count,
  defaultOpen,
  children,
}) => (
  <div className="collapse collapse-arrow bg-base-200 rounded-lg mb-3">
    <input type="checkbox" defaultChecked={defaultOpen} />
    <div className="collapse-title font-medium text-sm">
      {title} <span className="text-base-content/40 font-mono text-xs">({count} tools)</span>
    </div>
    <div className="collapse-content">{children}</div>
  </div>
);

const jiraGroups: ToolGroup[] = [
  {
    category: 'Projects',
    tools: [
      ['getJiraProjects', 'List all synced Jira projects'],
      ['createJiraProjects', 'Create a new Jira project'],
    ],
  },
  {
    category: 'Boards',
    tools: [
      ['getJiraBoards', 'List boards, optionally filtered by project'],
      ['getAllJiraBoards', 'List all boards across multiple projects'],
      ['getJiraBoardDetails', 'Get details of a specific board'],
      ['getJiraBoardConfiguration', 'Get column and status configuration for a board'],
      ['getJiraBoardEpics', 'List epics on a board'],
      ['getJiraBoardIssues', 'List issues on a board'],
      ['getJiraBoardsIssues', 'List issues across multiple boards'],
      ['getJiraBoardProperties', 'Get board properties and metadata'],
      ['getJiraAllSprints', 'List all sprints for a board'],
      ['getJiraAllSprintsForMultipleBoardsTool', 'List all sprints across multiple boards'],
      ['moveIssuesBoard', 'Move backlog issues onto the active board'],
    ],
  },
  {
    category: 'Issues',
    tools: [
      ['getJiraIssue', 'Fetch a single issue by key or ID'],
      ['getJiraIssueType', 'List available issue types for a project'],
      ['createJiraIssue', 'Create a single issue or epic'],
      ['createBulkJiraIssues', 'Create multiple issues in one call'],
      ['editJiraIssue', 'Edit issue fields (summary, description, priority, etc.)'],
      ['assignJiraIssue', 'Assign an issue to a user by accountId (or unassign with null)'],
      ['moveIssueToStatus', 'Move an issue to a target status by name'],
    ],
  },
  {
    category: 'Sprints',
    tools: [
      ['getJiraSprint', 'Get details for a specific sprint'],
      ['getJiraSprintIssues', 'List issues in a sprint'],
      ['createJiraSprint', 'Create a new sprint'],
      ['updateJiraSprint', 'Update sprint name, dates, goal, or state'],
      ['moveJiraIssuesToSprint', 'Add issues to a sprint'],
    ],
  },
  {
    category: 'Search, Users & Fields',
    tools: [
      ['searchUsersJira', 'Find Jira users by name or email'],
      ['getMyselfJira', 'Get current Jira user info'],
      ['changeLogJiraByJql', 'Search issue changelog and history via JQL'],
      ['getJiraCustomFields', 'List custom and system fields available in Jira'],
    ],
  },
  {
    category: 'Analytics & Sync',
    tools: [
      ['resolve_project', 'Resolve a Jira project key to its internal ID'],
      ['rag_search', 'Semantic search over synced Jira issues, sprints, and project data'],
      ['get_analytics', 'Fetch a metrics snapshot — project, user, studio, or sprint scope'],
      ['jira_sync', 'Trigger a Jira data synchronization (full or incremental)'],
    ],
  },
  {
    category: 'Notifications',
    tools: [
      ['telegramNotification', 'Send a Telegram notification'],
      ['slackNotification', 'Send a Slack notification'],
    ],
  },
];

const trelloGroups: ToolGroup[] = [
  {
    category: 'Boards & Lists',
    tools: [
      ['getTrelloBoards', "Get the user's Trello boards, or a board's lists"],
      ['createTrelloBoard', 'Create a new Trello board'],
      ['createTrelloList', 'Create a new list in a board'],
    ],
  },
  {
    category: 'Cards',
    tools: [
      ['getTrelloCards', 'Get cards from a board'],
      ['createTrelloCard', 'Create a new card'],
      ['updateTrelloCard', 'Update a card'],
      ['deleteTrelloCard', 'Delete a card'],
      ['moveTrelloCard', 'Move a card to another list'],
      ['checkTrelloCardStatus', 'Check the status of a card'],
      ['addTrelloComment', 'Add a comment to a card'],
      ['addTrelloLabel', 'Attach an existing label to a card'],
      ['addTrelloAttachment', 'Add an attachment to a card'],
    ],
  },
  {
    category: 'Search',
    tools: [
      [
        'searchTrelloContext',
        'Semantic search over board, list, and card data — falls back to the board bound to the current chat',
      ],
    ],
  },
];

const standaloneGroups: ToolGroup[] = [
  {
    category: 'Projects & Tasks',
    tools: [
      ['getStandaloneProjects', "Get all of the user's standalone projects"],
      ['createStandaloneProject', 'Create a new standalone project'],
      ['getStandaloneTasks', 'Get all tasks in a standalone project'],
      ['createStandaloneTask', 'Create a new task in a standalone project'],
      ['updateStandaloneTask', "Update a task's title, description, status, priority, or due date"],
      ['deleteStandaloneTask', 'Delete a standalone task'],
    ],
  },
  {
    category: 'Search',
    tools: [
      [
        'searchStandaloneContext',
        'Semantic search over standalone project and task data — falls back to the project bound to the current chat',
      ],
    ],
  },
];

const wizardGroups: ToolGroup[] = [
  {
    category: 'Proposal',
    tools: [
      [
        'saveWizardProposal',
        'Persist the spec analysis as the current draft proposal — methodology, project name, task breakdown, and initial docs',
      ],
      ['getWizardProposal', 'Fetch the current draft proposal, if one exists'],
      ['updateWizardMethodology', 'Change the methodology (kanban/scrum) on the draft'],
      [
        'confirmWizardProposal',
        'Lock the draft and build it out on the chosen platform — creates real data, cannot be undone',
      ],
    ],
  },
  {
    category: 'Tasks',
    tools: [
      ['addWizardProposalTask', 'Add a task to the draft proposal'],
      ['updateWizardProposalTask', 'Update a task on the draft proposal'],
      ['removeWizardProposalTask', 'Remove a task from the draft proposal'],
    ],
  },
  {
    category: 'Docs',
    tools: [
      ['addWizardProposalDoc', 'Add a doc (e.g. README, architecture note) to the draft proposal'],
      ['updateWizardProposalDoc', 'Update a doc on the draft proposal'],
      ['removeWizardProposalDoc', 'Remove a doc from the draft proposal'],
    ],
  },
];

const ToolReference: React.FC = () => (
  <div>
    <h2 className="text-2xl font-bold mb-2">Agent Tool Reference</h2>
    <p className="text-base-content/50 text-sm mb-5">Every tool each agent can call — for the curious.</p>
    <p className="text-sm text-base-content/70 mb-6 leading-relaxed">
      Each agent selects from its own tool set automatically based on what you ask — you never call these directly, just
      describe what you want in plain language. Expand a section to see everything a given agent can do.
    </p>

    <AgentCollapse title="Jira Agent" count={35} defaultOpen>
      <ToolTable groups={jiraGroups} />
    </AgentCollapse>

    <AgentCollapse title="Trello Agent" count={13}>
      <ToolTable groups={trelloGroups} />
    </AgentCollapse>

    <AgentCollapse title="Standalone Agent" count={7}>
      <ToolTable groups={standaloneGroups} />
    </AgentCollapse>

    <AgentCollapse title="Wizard Agent" count={10}>
      <ToolTable groups={wizardGroups} />
    </AgentCollapse>

    <Callout type="warning">
      Write operations (create, edit, status change, delete) are only performed when you explicitly instruct the agent.
      It will confirm what it is about to do before making changes when the scope is large.
    </Callout>

    <h3 className="font-semibold mt-6 mb-3">Example prompts</h3>
    <div className="space-y-2 mb-2">
      {[
        "Find all issues in To Do status that haven't moved in 10 days and create a subtask for each to investigate what is blocking.",
        "Create subtasks on every blocked issue asking why it's blocked.",
        'Close all issues in the current sprint that have been in Done for more than 3 days.',
        'Move all unfinished issues from the last sprint into the new one.',
        'Which team members have more than 5 open issues assigned right now?',
        'Create a new sprint starting next Monday with all high-priority issues from the backlog.',
      ].map((prompt) => (
        <div
          key={prompt}
          className="rounded-lg bg-base-200 shadow-card px-4 py-2.5 text-xs text-base-content/70 font-mono leading-relaxed"
        >
          &quot;{prompt}&quot;
        </div>
      ))}
    </div>
  </div>
);

export default ToolReference;
